import { Fragment } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import TextareaAutosize from '@mui/material/TextareaAutosize';

import { LayoutQuestion } from '../LayoutQuestion';

import styles from '../../Form/Form.module.css';

export default function TableQuestion(props) {
  const handleColumnChange = (index, field, value) => {
    const updatedOptions = [...(props.information.tableSetting?.options || [])];
    
    if (field === 'value') {
      updatedOptions[index] = {
        ...updatedOptions[index],
        value: value,
      };
    } else if (field === 'type') {
      updatedOptions[index] = {
        ...updatedOptions[index],
        type: value,
      };
    } else if (field === 'required') {
      updatedOptions[index] = {
        ...updatedOptions[index],
        required: value,
      };
    }
    
    props.setInformation({
      ...props.information,
      tableSetting: {
        ...props.information.tableSetting,
        options: updatedOptions,
      },
    });
  };

  const handleAddColumn = () => {
    const currentOptions = props.information.tableSetting?.options || [];
    if (currentOptions.length < 15) {
      props.setInformation({
        ...props.information,
        tableSetting: {
          ...props.information.tableSetting,
          options: [
            ...currentOptions,
            {
              required: false,
              type: 'text',
              value: '',
            },
          ],
        },
      });
    }
  };

  const handleRemoveColumn = (index) => {
    const updatedOptions = [...(props.information.tableSetting?.options || [])];
    if (updatedOptions.length > 1) {
      updatedOptions.splice(index, 1);
      
      props.setInformation({
        ...props.information,
        tableSetting: {
          ...props.information.tableSetting,
          options: updatedOptions,
        },
      });
    }
  };

  const getColumnValue = (option) => {
    return option?.value || '';
  };

  const getColumnType = (option) => {
    return option?.type || 'text';
  };

  const getColumnRequired = (option) => {
    return option?.required || false;
  };

  const tableOptions = props.information.tableSetting?.options || [];

  return (
   <LayoutQuestion {...props}>

      {/* Configuración de columnas */}
      <Box sx={{ mb: 2, mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Configuración de columnas
        </Typography>
        
        <Box
          sx={{
            backgroundColor: '#e8f0fe',
            padding: '0.8em',
            borderRadius: '6px',
            marginBottom: '1rem',
            fontSize: '0.85rem',
            color: '#333',
          }}
        >
          <strong>Nota:</strong> Los usuarios podrán agregar tantas filas como necesiten al responder la encuesta. 
          Solo necesitas definir las columnas que tendrá la tabla.
        </Box>

        {/* Columnas */}
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          Columnas de la tabla (mínimo 1, máximo 15)
        </Typography>

        {tableOptions.map((option, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '12px',
              mb: 2,
              backgroundColor: '#fafafa',
            }}
          >
            {/* Encabezado de la columna */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  minWidth: '35px',
                  height: '35px',
                  backgroundColor: '#F0F2F5',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  color: 'rgb(134, 140, 204)',
                  fontWeight: 600,
                }}
              >
                {index + 1}
              </Box>
              <TextField
                variant="standard"
                placeholder={`Nombre de columna ${index + 1}`}
                value={getColumnValue(option)}
                onChange={(e) => handleColumnChange(index, 'value', e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  disableUnderline: true,
                  style: { fontWeight: 500 },
                }}
                error={props.columnErrors?.[index]}
                helperText={props.columnErrors?.[index] ? 'La columna no puede estar vacía' : ''}
              />
              {tableOptions.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => handleRemoveColumn(index)}
                  sx={{ color: '#d32f2f' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* Configuración de validación y requerido */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {/* Tipo de validación */}
              <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                <InputLabel id={`validation-type-${index}`}>
                  Tipo de validación
                </InputLabel>
                <Select
                  labelId={`validation-type-${index}`}
                  value={getColumnType(option)}
                  label="Tipo de validación"
                  onChange={(e) => handleColumnChange(index, 'type', e.target.value)}
                >
                  <MenuItem value="text">Alfanumérico (texto libre)</MenuItem>
                  <MenuItem value="number">Solo números</MenuItem>
                  <MenuItem value="email">Solo correo electrónico</MenuItem>
                </Select>
              </FormControl>

              {/* Campo requerido */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={getColumnRequired(option)}
                    onChange={(e) => handleColumnChange(index, 'required', e.target.checked)}
                    size="small"
                  />
                }
                label="Campo requerido"
                sx={{ m: 0 }}
              />
            </Box>
          </Box>
        ))}

        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          {tableOptions.length < 15 && (
            <Button
              variant="text"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleAddColumn}
              sx={{
                backgroundColor: '#F7F7F7',
                width: '255px',
              }}
            >
              Añadir columna
            </Button>
          )}
        </Box>
      </Box>
    </LayoutQuestion>
  );
}